import React from "react";
import Surface, { type SurfaceTone } from "./Surface";
import { cn } from "../cn";

export type StatCardProps = {
  /** Short overline naming the metric. Four words or fewer. */
  label: string;
  /** The metric itself. `undefined` renders the loading placeholder. */
  value?: React.ReactNode;
  /** Secondary figure shown beside the value, e.g. `/ 100`. */
  unit?: React.ReactNode;
  /** A lucide icon element. Decorative — the label carries the meaning. */
  icon?: React.ReactNode;
  tone?: Extract<SurfaceTone, "default" | "accent" | "critical" | "warning" | "success" | "info">;
  /** A chip in the top-right, e.g. a percentage or a status word. */
  badge?: React.ReactNode;
  /** Detail rendered under the value — a progress bar, a breakdown, a caption. */
  children?: React.ReactNode;
  /** Accessible loading text, announced while `value` is undefined. */
  loadingLabel?: string;
  className?: string;
};

/** Icon tile colour per tone. Tint plus its matching ink, both token-driven. */
const ICON_TONE: Record<NonNullable<StatCardProps["tone"]>, string> = {
  default: "bg-surface-sunken text-content-secondary",
  accent: "bg-accent-tint text-accent-on-tint",
  critical: "bg-status-critical-tint text-status-critical-on-tint",
  warning: "bg-status-warning-tint text-status-warning-on-tint",
  success: "bg-status-success-tint text-status-success-on-tint",
  info: "bg-status-info-tint text-status-info-on-tint",
};

/**
 * A single metric on a dashboard.
 *
 * The numbers use `tabular-nums` so a row of cards does not jitter as values
 * update, and the card is flat-with-a-border: the previous stat cards carried
 * `shadow-premium` plus a `rounded-[4rem]` radius, which made every metric look
 * equally urgent and clipped the corners of the content on a narrow column.
 */
const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  unit,
  icon,
  tone = "default",
  badge,
  children,
  loadingLabel,
  className,
}) => {
  const isLoading = value === undefined;

  return (
    <Surface padding="md" className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {icon && (
            <span
              aria-hidden="true"
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-lg",
                ICON_TONE[tone]
              )}
            >
              {icon}
            </span>
          )}
          <span className="truncate text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
            {label}
          </span>
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>

      <div className="flex items-baseline gap-2">
        {isLoading ? (
          <span
            className="h-8 w-16 animate-pulse rounded-md bg-surface-sunken"
            role="status"
            aria-label={loadingLabel ?? label}
          />
        ) : (
          <span className="text-3xl font-semibold tabular-nums leading-none tracking-tight text-content-primary">
            {value}
          </span>
        )}
        {unit && !isLoading && (
          <span className="text-sm font-medium tabular-nums text-content-secondary">
            {unit}
          </span>
        )}
      </div>

      {children}
    </Surface>
  );
};

export default StatCard;
